import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import billingService from '../../api/billingService';
import customerService from '../../api/customerService';
import CompactPagination from '../../components/billing/CompactPagination';
import { can } from '../../Utility/Utility';

const canAssignMultiple = true;
function safeAssignToArray(v) {
  if (v == null) return [];
  if (Array.isArray(v)) return v.filter((x) => x != null && x !== '');
  const s = String(v).trim();
  if (!s) return [];
  if (s.includes(',')) return s.split(',').map((x) => x.trim()).filter(Boolean);
  return [s];
}
function safeAssignToString(v, fallback = 'Unassigned') {
  const arr = safeAssignToArray(v);
  return arr.length ? arr.join(', ') : fallback;
}

const CustomerList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const hasVisibleSidebar = !!searchParams.get('visiblesidbar');

  const IndustryOptions = [
    "DSA", "Education", "Real Estate", "Manufacturer", "Tour & Travels", 
    "Ayurveda", "Automobile", "Ecommerce", "Healthcare", "Agriculture", 
    "Consultancy", "Retail Store", "NGO", "Individual", "Software Development", 
    "IT Solutions", "Others"
  ];

  const [customers, setCustomers] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [userRole, setUserRole] = useState('owner');
  const [tableActions, setTableActions] = useState({});
  const [page, setPage] = useState(1);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadRecords, setUploadRecords] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const attachmentInputRef = useRef(null);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    assignTo: '',
    allocated: ''
  });

  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    company: '',
    accountType: 'GST',
    gstNumber: '',
    website: '',
    groupId: '',
    leadSource: 'Direct',
    company_size: '',
    industry: '',
    pincode: '',
    state: '',
    address: '',
    landmark: '',
    remark: '',
    status: 'active',
    assignTo: [],
    soldBy: ''
  });

  useEffect(() => {
    const initialize = async () => {
      await fetchMembers();
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log('userRole:', userRole);
    console.log('page:', page);
    if(page){
      fetchCustomers(filters.assignTo);
    }
    

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  const fetchMembers = async () => {
    try {
      const sessionRes = await customerService.getSession();
      if (sessionRes && sessionRes.success) {
        const userInfo = sessionRes.userInfo;
        setUserRole(userInfo.role);
        setTableActions(userInfo.permissions?.tableActions?.customers || {});
        
        // If not owner, force assignTo to user's email
        if (userInfo.role !== 'owner') {
          setFilters(prev => ({ ...prev, assignTo: userInfo.email }));
          fetchCustomers(userInfo.email);
        }else{
          fetchCustomers();
        }

        const response = await customerService.getAllMembers(userInfo.id);
        if (response && response.data && response.data.ActiveMembers) {
          setMembers(response.data.ActiveMembers);
        }
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const fetchCustomers = async (assignTo) => {
    try {
      setLoading(true);
      const effectiveAssignTo = assignTo ?? filters.assignTo;
      const res = await billingService.getCustomers({ 
        page, 
        limit, 
        search: filters.search, 
        status: filters.status,
        assignTo: effectiveAssignTo,
        allocated: filters.allocated
      });
      if (res.success) {
        setCustomers(res.data || []);
        setTotal(res.total || 0);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const applyFilters = (e) => {
    if (e) e.preventDefault();
    setPage(1);
    fetchCustomers();
  };

  const handleInputChange = (e) => {
    const target = e.target;
    const name = target.name;
    if (name === 'assignTo' && target.options && target.multiple) {
      const selected = Array.from(target.options).filter((opt) => opt.selected).map((opt) => opt.value);
      setFormData({ ...formData, [name]: selected });
      return;
    }
    const { value } = target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email && !formData.phone) {
      alert('Please provide either an email address or a phone number.');
      return;
    }

    try {
      const rawAssignTo = safeAssignToArray(formData.assignTo);
      const payload = { ...formData, attachments, assignTo: rawAssignTo };
      if (editingCustomer) {
        await billingService.updateCustomer(editingCustomer._id, payload);
      } else {
        await billingService.createCustomer(payload);
      }
      setShowModal(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      alert(error.response?.data?.message || error.message);
    }
  };

  const handleAttachmentChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingAttachments(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await customerService.uploadFile(fd);
        if (res && res.success && Array.isArray(res.data) && res.data.length > 0) {
          const item = res.data[0];
          uploaded.push({
            _id: item._id,
            file_name: item.fileName || item.file_name || file.name,
            file_size: item.fileSize ?? item.file_size ?? file.size,
            file_type: item.fileType || item.file_type || file.type,
            s3Location: item.s3Location || item.url,
            localPath: item.localPath,
            url: item.url || item.s3Location,
            uploaded_at: item.uploadedAt || item.uploaded_at || new Date().toISOString()
          });
        } else if (res && res.success && res.data && !Array.isArray(res.data)) {
          const item = res.data;
          uploaded.push({
            _id: item._id,
            file_name: item.fileName || item.file_name || file.name,
            file_size: item.fileSize ?? item.file_size ?? file.size,
            file_type: item.fileType || item.file_type || file.type,
            s3Location: item.s3Location || item.url,
            localPath: item.localPath,
            url: item.url || item.s3Location,
            uploaded_at: item.uploadedAt || item.uploaded_at || new Date().toISOString()
          });
        }
      }
      if (uploaded.length > 0) {
        setAttachments(prev => [...prev, ...uploaded]);
      }
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploadingAttachments(false);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleDownloadAttachment = async (att) => {
    try {
      await customerService.downloadAttachment(att);
    } catch (err) {
      alert('Download failed: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setAttachments(
      Array.isArray(customer?.attachments) ? customer.attachments :
      Array.isArray(customer?.documents) ? customer.documents :
      Array.isArray(customer?.Attachments) ? customer.Attachments :
      Array.isArray(customer?.Documents) ? customer.Documents : []
    );
    setFormData({
      firstname: customer.firstname || '',
      lastname: customer.lastname || '',
      email: customer.email || '',
      phone: customer.phone || '',
      company: customer.company || '',
      accountType: customer.accountType || 'GST',
      gstNumber: customer.gstNumber || '',
      website: customer.website || '',
      groupId: customer.groupId || '',
      leadSource: customer.leadSource || 'Direct',
      company_size: customer.company_size || '',
      industry: customer.industry || '',
      pincode: customer.pincode || '',
      state: customer.state || '',
      address: customer.address || '',
      landmark: customer.landmark || '',
      remark: customer.remark || '',
      status: customer.status || 'active',
      feedback: customer.feedback || '',
      usage: customer.usage || '',
      assignTo: safeAssignToArray(customer.assignTo),
      soldBy: customer.soldBy || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        const result = await billingService.deleteCustomer(id);
        if (result.success) fetchCustomers();
        else alert(result.message);
      } catch (error) {
        console.error('Error deleting customer:', error);
        alert(error.message);
      }
    }
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setAttachments([]);
    setFormData({
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      company: '',
      accountType: 'GST',
      gstNumber: '',
      website: '',
      groupId: '',
      leadSource: 'Direct',
      company_size: '',
      industry: '',
      pincode: '',
      state: '',
      address: '',
      landmark: '',
      remark: '',
      status: 'active',
      assignTo: [],
      soldBy: ''
    });
  };

  const exportToExcel = async () => {
    try {
      // Fetch all customers for export (ignoring pagination)
      const res = await billingService.getCustomers({ 
        limit: 5000, 
        search: filters.search, 
        status: filters.status,
        assignTo: filters.assignTo
      });
      
      if (res.success && res.data) {
        const exportData = res.data.map(c => ({
          customerId: c.customerId || c._id || '',
          accountType: c.accountType || '',
          address: c.address || '',
          assignTo: safeAssignToString(c.assignTo, ''),
          soldBy: c.soldBy || '',
          company: c.company || '',
          company_size: c.company_size || '',
          contactPerson: c.contactPerson || '',
          email: c.email || '',
          firstname: c.firstname || '',
          gstNumber: c.gstNumber || '',
          industry: c.industry || '',
          landmark: c.landmark || '',
          lastname: c.lastname || '',
          leadSource: c.leadSource || '',
          phone: c.phone || '',
          pincode: c.pincode || '',
          remark: c.remark || '',
          state: c.state || '',
          website: c.website || ''
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Customers");
        XLSX.writeFile(wb, `Customers_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);
        setUploadRecords(data);
      };
      reader.readAsBinaryString(file);
    }
  };

  const handleUploadSubmit = async () => {
    try {
      setIsUploading(true);
      const res = await billingService.uploadCustomers(uploadRecords);
      if (res.success) {
        alert('Upload started! You can check progress in System Logs.');
        setShowUploadModal(false);
        setUploadRecords([]);
        navigate('/system-logs');
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const downloadSample = () => {
    const sampleData = [
      {
        accountType: 'GST',
        address: '123 Main Street',
        assignTo: 'member@example.com',
        soldBy: 'member@example.com',
        company: 'Example Corp',
        company_size: '25',
        contactPerson: 'John Doe',
        email: 'john@example.com',
        firstname: 'John',
        gstNumber: '22AAAAA0000A1Z5',
        industry: 'IT Solutions',
        landmark: 'Near Market',
        lastname: 'Doe',
        leadSource: 'Direct',
        phone: '9876543210',
        pincode: '110001',
        remark: 'Priority customer',
        state: 'Delhi',
        website: 'https://example.com'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sample Format");
    XLSX.writeFile(wb, "Customer_Upload_Sample.xlsx");
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-700 mb-1">Customers</h4>
          <p className="text-muted small mb-0">Manage all your customer records in one place.</p>
        </div>
        <div className="d-flex gap-2">
          {((userRole === 'owner' || tableActions.upload !== false) && can('uploadFiles')) && (
            <button className="btn btn-light border shadow-sm px-4" onClick={() => setShowUploadModal(true)}>
              <i className="bi bi-upload me-2"></i> Upload
            </button>
          )}
          {((userRole === 'owner' || tableActions.export !== false) && can('exportFiles')) && (
            <button
              className="btn btn-light border shadow-sm px-4"
              onClick={exportToExcel}
            >
              <i className="bi bi-download me-2"></i> Export Excel
            </button>
          )}
          {can('addCustomer') && (
            <button 
              className="btn btn-primary shadow-sm px-4"
              onClick={() => { resetForm(); setShowModal(true); }}
            >
              <i className="bi bi-plus-lg me-2"></i> Add Customer
            </button>
          )}
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-header border-0 bg-transparent pt-4 px-4">
          <form className="row g-3" onSubmit={applyFilters}>
            <div className="col-md-4">
              <div className="input-group">
                <span className="input-group-text bg-light border-0"><i className="bi bi-search"></i></span>
                <input 
                  type="text" 
                  name="search"
                  className="form-control bg-light border-0" 
                  placeholder="Search name, phone, company..." 
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
            <div className="col-md-2">
              <select 
                name="status" 
                className="form-select bg-light border-0" 
                value={filters.status} 
                onChange={handleFilterChange}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="blocked">Blocked</option>
              </select>
            </div>
            <div className="col-md-2">
              <select 
                name="assignTo" 
                className="form-select bg-light border-0" 
                value={filters.assignTo} 
                onChange={handleFilterChange}
                disabled={userRole !== 'owner'}
              >
                <option value="">All Members</option>
                {members.map(member => (
                  <option key={member.username} value={member.username}>{member.username}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select 
                name="allocated" 
                className="form-select bg-light border-0" 
                value={filters.allocated} 
                onChange={handleFilterChange}
                disabled={userRole !== 'owner'}
              >
                <option value="">All Customers</option>
                <option value="assigned">Assigned Only</option>
                <option value="unassigned">Unassigned Only</option>
              </select>
            </div>
            <div className="col-md-2">
              <button type="submit" className="btn btn-light w-100">Filter</button>
            </div>
          </form>
        </div>
        <div className="table-responsive mt-3">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Assigned To</th>
                <th>Location</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center py-5">Loading...</td></tr>
              ) : customers.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-5">No customers found.</td></tr>
              ) : (
                customers.map((customer) => (
                  <tr key={customer._id}>
                    <td>
                      <div className="fw-600">{customer.firstname} {customer.lastname}</div>
                      <div className="text-xs text-muted">{customer.company || 'Individual'}</div>
                     
                    </td>
                    <td>{customer.email}</td>
                    <td>{customer.phone}</td>
                    <td>
                      <div className="text-xs text-nowrap">
                        {safeAssignToArray(customer.assignTo).length === 0 ? (
                          <span className="text-muted fst-italic">Unassigned</span>
                        ) : (
                          <div className="d-flex flex-wrap gap-1">
                            {safeAssignToArray(customer.assignTo).slice(0, 3).map((email, idx) => (
                              <span key={`${email}-${idx}`} className="badge bg-soft-primary text-primary rounded-pill">
                                {String(email).split('@')[0]}
                              </span>
                            ))}
                            {safeAssignToArray(customer.assignTo).length > 3 && (
                              <span className="badge bg-light text-muted rounded-pill" title={safeAssignToString(customer.assignTo, '')}>
                                +{safeAssignToArray(customer.assignTo).length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="text-xs">{customer.state || '-'}</div>
                      <div className="text-xs text-muted">{customer.pincode}</div>
                    </td>
                    <td>
                      <span className={`badge badge-soft-${customer.status === 'active' ? 'success' : 'danger'}`}>
                        {customer.status}
                      </span>
                    </td>
                    <td className="text-end">
                      <div className="d-flex justify-content-end gap-2">
                        {(userRole === 'owner' || tableActions.edit !== false) && (
                          <button className="btn btn-sm btn-light" onClick={() => handleEdit(customer)}><i className="bi bi-pencil"></i></button>
                        )}
                        {!hasVisibleSidebar && (userRole === 'owner' || tableActions.view !== false) && (
                          <button className="btn btn-sm btn-light text-primary" onClick={() => navigate(`/billing/customers/${customer._id}`)}><i className="bi bi-eye"></i></button>
                        )}
                        {(userRole === 'owner' || tableActions.delete !== false) && (
                          <button className="btn btn-sm btn-light text-danger" onClick={() => handleDelete(customer._id)}><i className="bi bi-trash"></i></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="card-footer bg-transparent border-0 py-4 px-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="text-muted small">Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} results</div>
              <CompactPagination
                page={page}
                total={total}
                limit={limit}
                onPageChange={setPage}
              />
            </div>
          </div>
        )}
      </div>

      {/* Customer Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content border-0">
              <div className="modal-header border-0">
                <h5 className="modal-title fw-bold">{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body p-4">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">First Name</label>
                      <input type="text" name="firstname" className="form-control" value={formData.firstname} onChange={handleInputChange} required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">Last Name</label>
                      <input type="text" name="lastname" className="form-control" value={formData.lastname} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">Email Address</label>
                      <input type="email" name="email" {...(editingCustomer ? {disabled: true} : {})} className="form-control" value={formData.email} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">Phone Number</label>
                      <input type="text" name="phone" className="form-control" value={formData.phone} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">Company Name</label>
                      <input type="text" name="company" className="form-control" value={formData.company} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted small fw-600">Account Type</label>
                      <select name="accountType" className="form-select" value={formData.accountType} onChange={handleInputChange}>
                        <option value="GST">GST</option>
                        <option value="Non-GST">Non-GST</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-muted small fw-600">GST Number</label>
                      <input type="text" name="gstNumber" className="form-control" value={formData.gstNumber} onChange={handleInputChange} />
                    </div>
                    <div className="col-12">
                      <label className="form-label text-muted small fw-600">Address</label>
                      <textarea name="address" className="form-control" rows="2" value={formData.address} onChange={handleInputChange}></textarea>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">Landmark</label>
                      <input type="text" name="landmark" className="form-control" value={formData.landmark} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">State</label>
                      <input type="text" name="state" className="form-control" value={formData.state} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">Pincode</label>
                      <input type="text" name="pincode" className="form-control" value={formData.pincode} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">Industry</label>
                      <select name="industry" className="form-select" value={formData.industry} onChange={handleInputChange}>
                        <option value="">Select Industry...</option>
                        {IndustryOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">Company Size</label>
                      <input type="text" name="company_size" className="form-control" value={formData.company_size} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">Website</label>
                      <input type="text" name="website" className="form-control" value={formData.website} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">Group ID</label>
                      <input type="text" name="groupId" className="form-control" placeholder="Optional" value={formData.groupId} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-8">
                      <label className="form-label text-muted small fw-600">Remark</label>
                      <input type="text" name="remark" className="form-control" value={formData.remark} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">Status</label>
                      <select name="status" className="form-select" value={formData.status} onChange={handleInputChange}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="blocked">Blocked</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">Assign To</label>
                      <select
                        name="assignTo"
                        className="form-select"
                        multiple
                        size="5"
                        value={Array.isArray(formData.assignTo) ? formData.assignTo : safeAssignToArray(formData.assignTo)}
                        onChange={handleInputChange}
                      >
                        {members.map(member => (
                          <option key={member.username} value={member.username}>{member.username}</option>
                        ))}
                      </select>
                      <div className="text-xs text-muted mt-1">Hold Ctrl/Cmd to select multiple members.</div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">Sold By</label>
                      <select name="soldBy" className="form-select" value={formData.soldBy} onChange={handleInputChange}>
                        <option value="">Unassigned</option>
                        {members.map(member => (
                          <option key={member.username} value={member.username}>{member.username}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">FEEDBACK</label>
                      <input type="text" className="form-control bg-light" value={formData.feedback || 'N/A'} disabled />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label text-muted small fw-600">USAGE</label>
                      <input type="text" className="form-control bg-light" value={formData.usage || 'N/A'} disabled />
                    </div>

                    {/* ATTACHMENTS / DOCUMENTS */}
                    <div className="col-12">
                      <label className="form-label text-muted small fw-600">
                        Attachments / Documents <span className="text-muted fw-400 fst-italic">(Optional)</span>
                        {uploadingAttachments && (
                          <span className="ms-2 text-success small">
                            <i className="bi bi-arrow-repeat spin me-1"></i> Uploading…
                          </span>
                        )}
                      </label>
                      <input
                        ref={attachmentInputRef}
                        type="file"
                        multiple
                        className="form-control mb-2"
                        onChange={handleAttachmentChange}
                        disabled={uploadingAttachments}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt"
                      />
                      {attachments.length > 0 && (
                        <ul className="list-group mb-2">
                          {attachments.map((att, idx) => {
                            const fileName = att.file_name || att.fileName || att.original_name || att.name || ((att.url || att.s3Location || att.localPath || '').split('/').pop().split('?')[0].split('#')[0]) || `Document ${idx + 1}`;
                            const rawSize = att.file_size ?? att.fileSize ?? att.size ?? null;
                            const sizeText = rawSize != null
                              ? (rawSize < 1024 * 1024
                                  ? `${Math.round(rawSize / 1024)} KB`
                                  : `${(rawSize / (1024 * 1024)).toFixed(1)} MB`)
                              : null;
                            return (
                              <li key={att._id ? `${att._id}-${idx}` : idx} className="list-group-item d-flex align-items-center justify-content-between py-2">
                                <div
                                  role="button"
                                  onClick={() => handleDownloadAttachment(att)}
                                  className="d-flex align-items-center gap-2 text-decoration-none text-reset min-w-0 flex-grow-1"
                                  style={{ cursor: 'pointer' }}
                                >
                                  <i className="bi bi-paperclip text-primary"></i>
                                  <span className="fw-600 text-truncate small" title={fileName}>{fileName}</span>
                                  {sizeText && <span className="text-muted small flex-shrink-0">({sizeText})</span>}
                                  <i className="bi bi-download text-muted ms-1 flex-shrink-0"></i>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger border-0"
                                  title="Remove"
                                  onClick={() => removeAttachment(idx)}
                                >
                                  <i className="bi bi-x-lg"></i>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      {attachments.length === 0 && (
                        <small className="text-muted fst-italic d-block mb-1">No files attached yet.</small>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0 p-4">
                  <button type="button" className="btn btn-light px-4" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary px-4">{editingCustomer ? 'Update Customer' : 'Save Customer'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header border-0 p-4 pb-0">
                <h5 className="modal-title fw-800">Upload Customers</h5>
                <button type="button" className="btn-close" onClick={() => setShowUploadModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="alert alert-info small d-flex justify-content-between align-items-center">
                  <span>
                    <i className="bi bi-info-circle-fill me-2"></i>
                    Upload up to 5000 records. Ensure 'phone' is unique.
                  </span>
                  <button type="button" className="btn btn-sm btn-link p-0 text-decoration-none fw-700" onClick={downloadSample}>
                    <i className="bi bi-download me-1"></i> Sample Format
                  </button>
                </div>
                <input 
                  type="file" 
                  className="form-control mb-3" 
                  accept=".xlsx, .xls, .csv"
                  onChange={handleFileUpload}
                />
                {uploadRecords.length > 0 && (
                  <div className="text-success small fw-600 mb-2">
                    {uploadRecords.length} records detected.
                  </div>
                )}
                {uploadRecords.length > 5000 && (
                  <div className="text-danger small fw-600">
                    Warning: Records exceed the 5000 limit.
                  </div>
                )}
              </div>
              <div className="modal-footer border-0 p-4 pt-0">
                <button type="button" className="btn btn-light px-4" onClick={() => setShowUploadModal(false)}>Cancel</button>
                <button 
                  type="button" 
                  className="btn btn-primary px-4" 
                  disabled={uploadRecords.length === 0 || uploadRecords.length > 5000 || isUploading}
                  onClick={handleUploadSubmit}
                >
                  {isUploading ? 'Starting...' : 'Start Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerList;
